import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import { EnrollmentFacade } from '@features/enrollment/facades/enrollment.facade';
import { MembershipFacade } from '@features/membership/facades/membership.facade';
import { Store } from '@ngrx/store';
import { getAuthUser } from '@rd/core/auth/store/selectors/auth.selectors';
import { TENANT_NAME } from '@rd/core/tokens/rd-lib.token';
import { EnrollmentHelper } from '@rd/helpers/enrollment.helper';
import { Enrollment, User } from '@rd/types';
import { isNotNull } from '@rd/utils/isNotNull';
import {
  ANZ_ACCOUNT_NUMBER_VALIDATION_REGEX,
  ANZ_BSB_VALIDATION_REGEX,
  NAME_VALIDATION_REGEX,
} from '@rd/utils/regex.utils';
import { EnrollmentFormType } from '@rd/wl/anz/types';
import { Observable } from 'rxjs';
import { filter, map, switchMap, takeWhile } from 'rxjs/operators';

export enum EnrollmentFormOptions {
  CreditCard,
  BankAccount,
}

@Component({
  selector: 'rd-anz-enrollment-form',
  templateUrl: './enrollment-form.component.html',
  styleUrls: ['./enrollment-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class ANZEnrollmentFormComponentComponent {
  user$: Observable<User> = this.store
    .select(getAuthUser)
    .pipe(filter(isNotNull));
  readonly formOptions = EnrollmentFormOptions;
  readonly enrollmentFormType = EnrollmentFormType;
  // making this public so that parent can use View Child query and
  // get the native value of the form
  public form = this.fb.group({
    selectedOption: this.fb.control(null),
    enrollment: this.fb.group({
      selected: this.fb.control(null),
    }),
    accountDetails: this.fb.group({
      accountFirstName: this.fb.control('', [
        Validators.required,
        Validators.pattern(NAME_VALIDATION_REGEX),
      ]),
      accountLastName: this.fb.control('', [
        Validators.required,
        Validators.pattern(NAME_VALIDATION_REGEX),
      ]),
      bsbNumber: this.fb.control('', [
        Validators.required,
        Validators.pattern(ANZ_BSB_VALIDATION_REGEX),
      ]),
      accountNumber: this.fb.control('', [
        Validators.required,
        Validators.pattern(ANZ_ACCOUNT_NUMBER_VALIDATION_REGEX),
      ]),
    }),
  });

  enrollments$: Observable<Enrollment[]> =
    this.enrollmentFacade.loadEnrollmentsLoading$.pipe(
      takeWhile((loading) => loading === true, true),
      filter((loading) => loading === false),
      switchMap(() => this.enrollmentFacade.enrollments$),
    );

  // Helper observable to make the template easier to reason about
  formType$: Observable<EnrollmentFormType> = this.enrollments$.pipe(
    map((enrollments: Enrollment[]) => {
      if (this.tenantName === 'anz') {
        return enrollments.length === 0
          ? EnrollmentFormType.NoCreditCardOnlyBankAccount
          : EnrollmentFormType.CreditCardAndBankAccount;
      }
      return enrollments.length === 0
        ? EnrollmentFormType.NoCreditCard
        : EnrollmentFormType.CreditCardOnly;
    }),
  );

  constructor(
    private fb: UntypedFormBuilder,
    private enrollmentFacade: EnrollmentFacade,
    private membershipFacade: MembershipFacade,
    private enrollmentHelper: EnrollmentHelper,
    private store: Store,
    @Inject(TENANT_NAME) private tenantName: 'anz' | 'anz-biz',
  ) {}

  get selectedOption(): UntypedFormControl {
    return this.form.get('selectedOption') as UntypedFormControl;
  }

  displayNumber(enrollment: Enrollment): string {
    return this.enrollmentHelper.displayedAccountNumber(
      enrollment.groupReferenceId,
    );
  }
}
